import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import '../../App.css';
import CSS from 'csstype';
import { useTranslation } from 'react-i18next';
import { useAppSelector, useAppDispatch } from '../../app/hooks';

// icons
import { BellRing, Settings, Square, SquareCheckBig, SunMoon } from 'lucide-react';

// types
import { Project, Site, Theme, UserType } from '../../utils/types';

// types relating to events.
import { Event, EventType, EventTarget, GatewaySettingsType } from '../../utils/events';

// functions/
import { siteName } from '../../utils/functions';

// classes
import { ToolButtonType } from '../../tools/tool-button';
import ToolButton from '../../tools/tool-button';
import Tool from '../../tools/tool';
import Scrollbox from '../../tools/scrollbox';
import { getFocusableElements } from '../../utils/accessibility';
import { CheckBox } from '../../tools/controls';
import { handleEvent } from '../../app-actions';

//	--------------------------------------------------------------------------
//
//	T Y P E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	P R O P E R T I E S
//
//	--------------------------------------------------------------------------

//	--------------------------------------------------------------------------
//
//	H T M L   C U S T O M   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

function DrawWindow(props: {
  foreground: string;
  background: string;
  value: string;
  text: string;
  checked?: boolean;
}) {
  //
  // translation function
  const { t } = useTranslation();

  return (
    <>
      <div
        style={{
          flex: '1 1',
          width: 'auto',
          backgroundColor: props.background,
          border: '1pt solid ' + props.foreground,
          borderRadius: '10px 10px 0px 0px',
          margin: '10px 10px 0px 10px',
        }}
      />
      <div
        style={{
          flex: '3 3',
          width: 'auto',
          backgroundColor: props.background,
          border: '1pt solid ' + props.foreground,
          borderRadius: '0px 0px 10px 10px',
          margin: '0px 10px 0px 10px',
        }}
      />
      <div
        className="text-colour"
        style={{
          flex: '0 0 60px',
          display: 'flex',
          flexDirection: 'row',
          textAlign: 'center',
          alignItems: 'center',
          marginLeft: '5px',
        }}
      >
        <input
          type="radio"
          value={props.value}
          checked={props.checked === true}
          style={{ margin: '5px' }}
        />
        {t(props.text)}
      </div>
    </>
  );
  //
} // DrawWindow

//	--------------------------------------------------------------------------
//
//	C L A S S   D E F I N I T I O N
//
//	--------------------------------------------------------------------------

export default function Preferences(props: { onClose: () => void; projects: Project[] }) {
  //
  //	--------------------------------------------------------------------------
  //
  //	C O N S T A N T S
  //
  //	--------------------------------------------------------------------------

  const NUM_FONT_SIZES: number = 4;
  const FONT_SIZE: { size: number; pt: number; text: string }[] = [
    { size: 0, pt: 10, text: 'Smaller' },
    { size: 1, pt: 12, text: 'Normal' },
    { size: 2, pt: 14, text: 'Bigger' },
    { size: 3, pt: 16, text: 'Biggest' },
  ];
  const SLIDER_GAP: number = 100.0;
  const SLIDER_PADDING: number = 50.0;

  const dispatch = useAppDispatch();

  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  // side panels maximised, or not?
  const sS_theme: Theme | undefined = useAppSelector((state) => state.settings.theme);
  const sS_fontSize: number | undefined = getFontSizeIndex({
    fontSize: useAppSelector((state) => state.settings.fontSize),
  });
  const sS_defaultProject: number | undefined = useAppSelector(
    (state) => state.settings.defaultProject,
  );
  const sS_defaultSiteUUID: string | undefined = useAppSelector(
    (state) => state.settings.defaultSiteUUID,
  );
  const [sS_popupNotificationsEnabled, setPopupNotificationsEnabled] = useState(
    useAppSelector((state) => state.settings.popupNotificationsEnabled),
  );

  const [sS_popupLogsEnabled, setPopupLogsEnabled] = useState(
    useAppSelector((state) => state.settings.popupLogsEnabled),
  );

  // global state variables.
  const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);
  const sG_userType: UserType | undefined = useAppSelector(
    (state) => state.global.userType,
  );

  // data state variables.
  const sD_sites: Site[] = useAppSelector((state) => state.data.sites);

  //	------------------------------------------------------------
  //
  //	Handler for changes to buttons
  //
  //	------------------------------------------------------------

  enum SettingsPage {
    APPEARANCE,
    NOTIFICATIONS,
    OTHER_SETTINGS,
  } // SETTINGS_PAGE

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  const [sSettingsPage, setSettingsPage] = useState<SettingsPage>(
    SettingsPage.APPEARANCE,
  );

  const rDragging = useRef<{
    dragging: boolean;
    position: number;
    originalFontSize: number;
  }>({
    dragging: false,
    position: 0,
    originalFontSize: 0,
  });

  useEffect(() => {
    setPopupNotificationsEnabled(sS_popupNotificationsEnabled);
  }, [sS_popupNotificationsEnabled]);

  useEffect(() => {
    setPopupLogsEnabled(sS_popupLogsEnabled);
  }, [sS_popupLogsEnabled]);

  //	------------------------------------------------------------
  //
  //	Find the appropriate index for a particular font size.
  //
  //	------------------------------------------------------------

  function getFontSizeIndex(args: { fontSize?: number }) {
    //
    let index: number = 1;

    if (args.fontSize !== undefined) {
      index = FONT_SIZE.findIndex((item) => item.pt === args.fontSize);
    }

    // if not found then use default value.
    if (index < 0 || index > NUM_FONT_SIZES - 1) {
      index = 1;
    }

    // return something.
    return index;
    //
  } // getFontSizeIndex

  //	------------------------------------------------------------
  //
  //	Button handler for check box click events.
  //
  //	------------------------------------------------------------

  const checkChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    //
    const inputBox = event.currentTarget;

    // update the state after the checkbox component change.
    if (inputBox.name === 'popup-notifications-enabled') {
      const checked: boolean = !sS_popupNotificationsEnabled;
      setPopupNotificationsEnabled(checked);
      updatePreferences({
        popupNotificationsEnabled: checked,
      });
    }

    // update the state after the checkbox component change.
    if (inputBox.name === 'popup-logs-enabled') {
      const checked: boolean = !sS_popupLogsEnabled;
      setPopupLogsEnabled(checked);
      updatePreferences({
        popupLogsEnabled: checked,
      });
    }
    //
  }; // checkChange

  //	------------------------------------------------------------
  //
  //	Handler for select box and check box onChange event.
  //
  //	------------------------------------------------------------

  const onChangeHandler = (
    event: React.ChangeEvent<HTMLSelectElement> | React.ChangeEvent<HTMLInputElement>,
  ) => {
    //
    const inputBox = event.target;

    // update the default project.
    if (inputBox.name === 'lst-default-project') {
      updatePreferences({ defaultProject: Number(inputBox.value) });
    }

    // update the default site.
    if (inputBox.name === 'lst-default-site') {
      updatePreferences({ defaultSiteUUID: inputBox.value });
    }
    //
  }; // onChangeHandler

  //	------------------------------------------------------------
  //
  //	Handler for div clicks
  //
  //	------------------------------------------------------------

  const onClickDivHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    //
    const divBox: HTMLDivElement = event.currentTarget;

    // if one of the site buttons has been pressed then update the currently selected site.
    if (divBox.id === 'font-slider-bar') {
      const parent: HTMLElement | null = divBox.parentElement;
      const left: number = parent?.offsetLeft ?? 0;
      let fontSize: number = Math.round(
        (event.clientX - left - SLIDER_PADDING) / SLIDER_GAP,
      );
      if (fontSize < 0) {
        fontSize = 0;
      }
      if (fontSize > NUM_FONT_SIZES - 1) {
        fontSize = NUM_FONT_SIZES - 1;
      }
      //setFontSize(fontSize);
      updatePreferences({ fontSize: fontSize });
    }
    //
  }; // onClickDivHandler

  //	------------------------------------------------------------
  //
  //	Handler for clicks to buttons
  //
  //	------------------------------------------------------------

  const onClickHandler = (event: React.MouseEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.currentTarget;

    if (inputBox.name === 'close-preferences') {
      props.onClose();
    }
    //
  }; // onClickHandler

  //	------------------------------------------------------------
  //
  //	Mouse-down handler for div elements.
  //
  //	------------------------------------------------------------

  const onMouseDownHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    //
    const divElement: HTMLDivElement = event.currentTarget;

    // handle the font slider being moved.
    if (divElement.id === 'font-slider-handle') {
      rDragging.current.dragging = true;
      rDragging.current.position = event.clientX;
      rDragging.current.originalFontSize = sS_fontSize ?? 1;
    }
    //
  }; // onMouseDownHandler

  //	------------------------------------------------------------
  //
  //	Mouse-leave handler for div elements.
  //
  //	------------------------------------------------------------

  const onMouseLeaveHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    //
    const divElement: HTMLDivElement = event.currentTarget;

    // stop the dragging event if the mouse leaves the boundary of the container div.
    if (rDragging.current.dragging === true) {
      rDragging.current.dragging = false;
    }
    //
  }; // onMouseLeaveHandler

  //	------------------------------------------------------------
  //
  //	Mouse-move handler for div elements.
  //
  //	------------------------------------------------------------

  const onMouseMoveHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    //
    const divElement: HTMLDivElement = event.currentTarget;

    // handle the font slider being moved.
    if (divElement.id === 'font-slider' && rDragging.current.dragging === true) {
      let fontSize: number =
        Math.round((event.clientX - rDragging.current.position) / SLIDER_GAP) +
        rDragging.current.originalFontSize;
      if (fontSize < 0) {
        fontSize = 0;
      }
      if (fontSize > NUM_FONT_SIZES - 1) {
        fontSize = NUM_FONT_SIZES - 1;
      }
      //setFontSize(fontSize);
      updatePreferences({ fontSize: fontSize });
    }
    //
  }; // onMouseMoveHandler

  //	------------------------------------------------------------
  //
  //	Mouse-up handler for div elements.
  //
  //	------------------------------------------------------------

  const onMouseUpHandler = (event: React.MouseEvent<HTMLDivElement>) => {
    //
    const divElement: HTMLDivElement = event.currentTarget;

    // handle the font slider being moved.
    if (divElement.id === 'font-slider') {
      rDragging.current.dragging = false;
    }
    //
  }; // onMouseUpHandler

  //	------------------------------------------------------------
  //
  //	Update the preferences to the database.
  //
  //	------------------------------------------------------------

  function updatePreferences(args: {
    theme?: Theme;
    fontSize?: number;
    defaultProject?: number;
    defaultSiteUUID?: string;
    popupNotificationsEnabled?: boolean;
    popupLogsEnabled?: boolean;
  }) {
    //
    let params: GatewaySettingsType = {};

    // update theme
    if (args.theme !== undefined && args.theme !== sS_theme) {
      params = {
        ...params,
        ...{
          theme: args.theme,
        },
      };
    }

    // update font size
    if (args.fontSize !== sS_fontSize && args.fontSize !== undefined) {
      params = {
        ...params,
        ...{
          fontSize: FONT_SIZE[args.fontSize].pt,
        },
      };
    }

    // update default project
    if (args.defaultProject !== undefined && args.defaultProject !== sS_defaultProject) {
      params = {
        ...params,
        ...{
          defaultProject: args.defaultProject,
        },
      };
    }

    // update default site
    if (
      args.defaultSiteUUID !== undefined &&
      args.defaultSiteUUID !== sS_defaultSiteUUID
    ) {
      params = {
        ...params,
        ...{
          defaultSiteUUID: args.defaultSiteUUID,
        },
      };
    }

    // update notifications pop-ups flag
    if (
      args.popupNotificationsEnabled !== undefined &&
      args.popupNotificationsEnabled !== sS_popupNotificationsEnabled
    ) {
      params = {
        ...params,
        ...{
          popupNotificationsEnabled: args.popupNotificationsEnabled,
        },
      };
    }

    // update errors pop-ups flag
    if (
      args.popupLogsEnabled !== undefined &&
      args.popupLogsEnabled !== sS_popupLogsEnabled
    ) {
      params = {
        ...params,
        ...{
          popupLogsEnabled: args.popupLogsEnabled,
        },
      };
    }

    // raise the event, only if we've got at least one parameter.
    if (Object.keys(params).length > 0) {
      const newEvent: Event = {
        eventTarget: EventTarget.APP,
        eventType: EventType.SET_PREFERENCES,
        parameters: params,
      };
      dispatch(handleEvent({ event: newEvent }));
    }
    //
  } // updatePreferences

  //	--------------------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	--------------------------------------------------------------------------

  const currentFontIndex = Math.min(Math.max(sS_fontSize ?? 1, 0), NUM_FONT_SIZES - 1);

  const [fontIndex, setFontIndex] = useState<number>(currentFontIndex);

  useEffect(() => {
    setFontIndex(currentFontIndex);
  }, [currentFontIndex]);

  const applyFontSizeIndex = (nextIndex: number) => {
    const clamped = Math.min(Math.max(nextIndex, 0), NUM_FONT_SIZES - 1);
    if (clamped !== fontIndex) {
      setFontIndex(clamped);
    }
    if (clamped !== currentFontIndex) {
      updatePreferences({ fontSize: clamped });
    }
  };

  const changeFontSizeBy = (delta: number) => {
    applyFontSizeIndex(fontIndex + delta);
  };

  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) {
      return;
    }

    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const focusFirstElement = () => {
      const focusable = getFocusableElements(dialog);
      const target = focusable[0] ?? dialog;
      window.requestAnimationFrame(() => {
        target.focus();
      });
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        props.onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }

      const focusable = getFocusableElements(dialog);
      if (focusable.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement as HTMLElement | null;

      if (event.shiftKey) {
        if (activeElement === first || !dialog.contains(activeElement)) {
          event.preventDefault();
          last.focus();
        }
      } else if (activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    dialog.addEventListener('keydown', handleKeyDown);
    focusFirstElement();

    return () => {
      dialog.removeEventListener('keydown', handleKeyDown);
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, []);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="preferences-title"
      tabIndex={-1}
      style={{
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ flex: '0 0 5px' }} />
      <div
        style={{
          flex: '1 1',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'center',
        }}
      >
        <div
          className="br-d-r"
          data-maximised="T"
          data-dark-background="T"
          style={{
            flex: '0 0 200px',
            height: '100%',
            minHeight: '450px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div style={{ flex: '0 0 20px' }} />
          <div
            style={{
              flex: '1 1',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'left',
            }}
          >
            <Tool
              name="appearance"
              text="Appearance"
              LucideIcon={SunMoon}
              onClick={() => setSettingsPage(SettingsPage.APPEARANCE)}
              selected={sSettingsPage === SettingsPage.APPEARANCE}
              alignLeft={true}
            />
            <div style={{ flex: '0 0 20px' }} />
            <Tool
              name="notifications"
              text="Notifications"
              LucideIcon={BellRing}
              onClick={() => setSettingsPage(SettingsPage.NOTIFICATIONS)}
              selected={sSettingsPage === SettingsPage.NOTIFICATIONS}
              alignLeft={true}
            />
            <div style={{ flex: '0 0 20px' }} />
            <Tool
              name="other-settings"
              text="Other settings"
              LucideIcon={Settings}
              onClick={() => setSettingsPage(SettingsPage.OTHER_SETTINGS)}
              selected={sSettingsPage === SettingsPage.OTHER_SETTINGS}
              alignLeft={true}
            />
            <div style={{ flex: '1 1' }} />
          </div>
          <div style={{ flex: '0 0 20px' }} />
        </div>
        <div style={{ flex: '0 0 20px' }} />
        <div
          role="region"
          aria-label="Appearance"
          style={{
            flex: '1 1',
            display: sSettingsPage === SettingsPage.APPEARANCE ? 'flex' : 'none',
            flexDirection: 'column',
          }}
        >
          <Scrollbox direction="vertical" flex="1 1" width="100%" flexDirection="column">
            {/*
               // theme.
               */}
            <div className="text-colour" style={{ flex: '0 0 auto' }}>
              {t('Theme')}:
            </div>
            <div style={{ flex: '0 0 15px' }} />
            <div style={{ flex: '0 0 180px', display: 'flex', flexDirection: 'row' }}>
              <button
                className="text-colour"
                onClick={() => updatePreferences({ theme: Theme.LIGHT })}
                style={{
                  flex: '0 0 110px',
                  display: 'flex',
                  flexDirection: 'column',
                  border:
                    sS_theme === Theme.LIGHT ? '2pt solid' : '2pt solid transparent',
                  borderRadius: '10px',
                  backgroundColor: 'transparent',
                }}
              >
                <DrawWindow
                  foreground="black"
                  background="white"
                  value="lightMode"
                  text="Light mode"
                  checked={sS_theme === Theme.LIGHT}
                />
              </button>
              <div style={{ flex: '0 0 20px' }} />
              <button
                className="text-colour"
                onClick={() => updatePreferences({ theme: Theme.DARK })}
                style={{
                  flex: '0 0 110px',
                  display: 'flex',
                  flexDirection: 'column',
                  border: sS_theme === Theme.DARK ? '2pt solid' : '2pt solid transparent',
                  borderRadius: '10px',
                  backgroundColor: 'transparent',
                }}
              >
                <DrawWindow
                  foreground="white"
                  background="black"
                  value="darkMode"
                  text="Dark mode"
                  checked={sS_theme === Theme.DARK}
                />
              </button>
              <div style={{ flex: '0 0 20px' }} />
              <button
                className="text-colour"
                onClick={() => updatePreferences({ theme: Theme.AUTO })}
                style={{
                  flex: '0 0 110px',
                  display: 'flex',
                  flexDirection: 'column',
                  border: sS_theme === Theme.AUTO ? '2pt solid' : '2pt solid transparent',
                  borderRadius: '10px',
                  backgroundColor: 'transparent',
                }}
              >
                <DrawWindow
                  foreground="white"
                  background="#888888"
                  value="autoMode"
                  text="Automatic"
                  checked={sS_theme === Theme.AUTO}
                />
              </button>
            </div>
            <div style={{ flex: '0 0 25px' }} />

            {/*
               // font size.
               */}
            <div
              id="preferences-font-size-label"
              className="text-colour"
              style={{ flex: '0 0 auto' }}
            >
              {t('Font size')}:
            </div>
            <div style={{ flex: '0 0 15px' }} />
            <div
              id="font-slider"
              role="slider"
              aria-valuenow={FONT_SIZE[sS_fontSize].pt}
              aria-valuetext={FONT_SIZE[sS_fontSize].text}
              aria-valuemin={FONT_SIZE[0].pt}
              aria-valuemax={FONT_SIZE[NUM_FONT_SIZES - 1].pt}
              aria-orientation="horizontal"
              aria-labelledby="preference-font-size-label"
              style={{
                flex: '0 0 100px',
                width:
                  ((NUM_FONT_SIZES - 1) * SLIDER_GAP + SLIDER_PADDING * 2).toString() +
                  'px',
                position: 'relative',
                userSelect: 'none',
              }}
              onMouseMove={onMouseMoveHandler}
              onMouseUp={onMouseUpHandler}
              onMouseLeave={onMouseLeaveHandler}
            >
              <div
                id="font-slider-bar"
                style={{
                  position: 'absolute',
                  top: '37px',
                  left: (SLIDER_PADDING - 20).toString() + 'px',
                  border: '1pt solid ' + (sG_darkMode ? 'white' : 'black'),
                  backgroundColor: 'transparent',
                  width: ((NUM_FONT_SIZES - 1) * SLIDER_GAP + 41).toString() + 'px',
                  height: '8px',
                  borderRadius: '4px',
                  cursor: 'pointer',
                }}
                onClick={onClickDivHandler}
              />
              {FONT_SIZE.map((size, sizeIdx) => (
                <>
                  <div
                    style={{
                      position: 'absolute',
                      top: '25px',
                      left: (SLIDER_PADDING + sizeIdx * SLIDER_GAP).toString() + 'px',
                      border: 'none',
                      backgroundColor: sG_darkMode ? 'white' : 'black',
                      width: '1px',
                      height: '9px',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '48px',
                      left: (SLIDER_PADDING + sizeIdx * SLIDER_GAP).toString() + 'px',
                      border: 'none',
                      backgroundColor: sG_darkMode ? 'white' : 'black',
                      width: '1px',
                      height: '9px',
                    }}
                  />
                  <div
                    style={{
                      position: 'absolute',
                      top: '65px',
                      left:
                        (
                          SLIDER_PADDING -
                          SLIDER_GAP / 2 +
                          sizeIdx * SLIDER_GAP
                        ).toString() + 'px',
                      border: 'none',
                      backgroundColor: 'transparent',
                      width: SLIDER_GAP.toString() + 'px',
                      height: '30px',
                      color: sG_darkMode ? 'white' : 'black',
                      fontSize: size.pt.toString() + 'pt',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {size.text}
                  </div>
                </>
              ))}
              <div
                id="font-slider-handle"
                className="background-colour"
                role="slider"
                tabIndex={0}
                aria-valuemin={0}
                aria-valuemax={NUM_FONT_SIZES - 1}
                aria-valuenow={fontIndex}
                aria-valuetext={FONT_SIZE[fontIndex].text}
                style={{
                  position: 'absolute',
                  top: '29px',
                  left: (fontIndex * SLIDER_GAP + SLIDER_PADDING - 11).toString() + 'px',
                  border: '2pt solid #e70068',
                  width: '24px',
                  height: '24px',
                  borderRadius: '12px',
                  cursor: 'pointer',
                }}
                onMouseDown={(event) => {
                  onMouseDownHandler(event);
                  event.currentTarget.focus();
                }}
                onKeyDown={(event) => {
                  if (
                    event.key === 'ArrowLeft' ||
                    event.key === 'ArrowDown' ||
                    event.key === 'PageDown'
                  ) {
                    event.preventDefault();
                    changeFontSizeBy(-1);
                  }
                  if (
                    event.key === 'ArrowRight' ||
                    event.key === 'ArrowUp' ||
                    event.key === 'PageUp'
                  ) {
                    event.preventDefault();
                    changeFontSizeBy(1);
                  }
                  if (event.key === 'Home') {
                    event.preventDefault();
                    if (fontIndex !== 0) {
                      applyFontSizeIndex(0);
                    }
                  }
                  if (event.key === 'End') {
                    event.preventDefault();
                    if (fontIndex !== NUM_FONT_SIZES - 1) {
                      applyFontSizeIndex(NUM_FONT_SIZES - 1);
                    }
                  }
                }}
              />
            </div>

            <div style={{ flex: '1 1' }} />
            <div style={{ flex: '0 0 20px' }} />
          </Scrollbox>
        </div>
        <div
          role="region"
          aria-label="Notifications"
          style={{
            flex: '1 1',
            display: sSettingsPage === SettingsPage.NOTIFICATIONS ? 'flex' : 'none',
            flexDirection: 'column',
          }}
        >
          <Scrollbox direction="vertical" flex="1 1" width="100%" flexDirection="column">
            {/*
                   // enable or not  notifications pop-ups.
                   */}
            <button
              name="popup-notifications-enabled"
              role="checkbox"
              aria-label="Enabled or disabled pop-up notifications"
              aria-checked={sS_popupNotificationsEnabled}
              type="button"
              title={t('Enable pop-up notifications')}
              className="action-button text-colour"
              onClick={checkChange}
              style={{
                display: 'flex',
                flexDirection: 'row',
                height: '30px',
                marginTop: '10px',
                gap: '10px',
              }}
            >
              {sS_popupNotificationsEnabled ? (
                <SquareCheckBig width={25} height={25} strokeWidth={1.5} />
              ) : (
                <Square width={25} height={25} strokeWidth={1.5} />
              )}
              {t('Enable pop-up notifications')}
            </button>
            <button
              name="popup-logs-enabled"
              role="checkbox"
              aria-label="Enabled or disabled pop-up notifications for logs"
              aria-checked={sS_popupLogsEnabled}
              type="button"
              title={t('Enable pop-up logs')}
              className="action-button text-colour"
              onClick={checkChange}
              style={{
                display: 'flex',
                flexDirection: 'row',
                height: '30px',
                marginTop: '10px',
                gap: '10px',
              }}
            >
              {sS_popupLogsEnabled ? (
                <SquareCheckBig width={25} height={25} strokeWidth={1.5} />
              ) : (
                <Square width={25} height={25} strokeWidth={1.5} />
              )}
              {t('Enable pop-up logs')}
            </button>
          </Scrollbox>
        </div>
        <div
          role="region"
          aria-label="Other settings"
          style={{
            flex: '1 1',
            display: sSettingsPage === SettingsPage.OTHER_SETTINGS ? 'flex' : 'none',
            flexDirection: 'column',
          }}
        >
          <Scrollbox direction="vertical" flex="1 1" width="100%" flexDirection="column">
            {/*
               // default project.
               */}
            <div
              id="preferences-default-project-label"
              className="text-colour"
              style={{ flex: '0 0 auto' }}
            >
              {t('Default project')}:
            </div>
            <div style={{ marginTop: '10px', width: '250px', marginBottom: '15px' }}>
              <select
                aria-labelledby="preferences-default-project-label"
                name="lst-default-project"
                className="listbox"
                value={sS_defaultProject}
                multiple={false}
                onChange={onChangeHandler}
                data-placeholder-shown={sS_defaultProject === undefined ? 'T' : 'F'}
              >
                <option hidden value="">
                  {t('Select default project')}
                </option>
                {props.projects.map((item, index) => (
                  <option key={item.abbreviation} label={item.name} value={item.id}>
                    {item.name}
                  </option>
                ))}
              </select>
            </div>

            {/*
               // default site.
               */}
            {sG_userType === UserType.DEVELOPER && (
              <>
                <div
                  id="preferences-default-site-label"
                  className="text-colour"
                  style={{ flex: '0 0 auto' }}
                >
                  {t('Default site')}:
                </div>
                <div style={{ marginTop: '10px', width: '250px', marginBottom: '15px' }}>
                  <select
                    aria-labelledby="preferences-default-site-label"
                    name="lst-default-site"
                    className="listbox"
                    value={sS_defaultSiteUUID}
                    multiple={false}
                    onChange={onChangeHandler}
                    data-placeholder-shown={sS_defaultSiteUUID === undefined ? 'T' : 'F'}
                  >
                    <option hidden value="">
                      {t('Select default site')}
                    </option>
                    {sD_sites.map((item, index) => (
                      <option
                        key={item.site_uuid}
                        label={siteName({ site: item })}
                        value={item.site_uuid}
                      >
                        {siteName({ site: item })}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
          </Scrollbox>
        </div>
      </div>

      <div
        style={{
          flex: '0 0 60px',
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
        }}
      >
        <div style={{ flex: '1 1' }} />
        <ToolButton
          key={t('Close')}
          name="close-preferences"
          onClick={onClickHandler}
          text={t('Close')}
          type={ToolButtonType.SECONDARY}
        />
        <div style={{ flex: '0 0 15px' }} />
      </div>
    </div>
  );
} // Preferences
